import { useEffect, useRef } from 'react';
import { useMount } from 'react-use';

import { UserStorageConversation } from 'apis/userStorage';
import {
  defaultCustomerInfoOrder,
  defaultKnowledgeOrder,
  defaultSectionOrder,
} from 'components/conversation/components/sidebar/conversationSidebar.types';
import { cardKey } from 'components/conversation/components/sidebar/utils/cardKey';
import { mergeOrder } from 'components/conversation/components/sidebar/utils/reorder';
import { getLocalSettings } from 'components/settings/api/personal';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useServerStorage } from 'utils/useServerStorage';

type ConversationStorage = NonNullable<UserStorageConversation['conversation']>;

/**
 * Non-destructively folds a user's legacy per-section sidebar settings into the
 * flat `sidebar-cards-admin-management` model. The legacy keys are read but left
 * untouched (so turning the flag off restores the old sidebar exactly), and the
 * flat keys are written only once.
 *
 * Section order is flattened in place; within-section card order is preserved;
 * hidden items carry over. Hidden whole-sections expand to their known static
 * member cards (native/custom section hides can't be enumerated here and are
 * skipped — a rare case).
 */
const buildFlatFromLegacy = (
  conversation: ConversationStorage,
): {
  readonly order: string[];
  readonly overrides: Record<string, boolean>;
} | null => {
  const hasLegacy =
    conversation.sidebarSectionOrder ||
    conversation.sidebarCustomerInfoOrder ||
    conversation.sidebarKnowledgeOrder ||
    conversation.sidebarIntegrationOrder ||
    conversation.sidebarCustomIntegrationOrder ||
    conversation.sidebarHiddenItems;

  if (!hasLegacy) {
    return null;
  }

  const order: string[] = [];
  const seen = new Set<string>();
  const push = (key: string) => {
    if (!seen.has(key)) {
      seen.add(key);
      order.push(key);
    }
  };

  mergeOrder(conversation.sidebarSectionOrder, defaultSectionOrder).forEach(
    (section) => {
      if (section === 'customerInfo') {
        mergeOrder(
          conversation.sidebarCustomerInfoOrder,
          defaultCustomerInfoOrder,
        ).forEach((id) => push(cardKey({ cardType: 'static', cardId: id })));
      } else if (section === 'knowledge') {
        mergeOrder(
          conversation.sidebarKnowledgeOrder,
          defaultKnowledgeOrder,
        ).forEach((id) => push(cardKey({ cardType: 'static', cardId: id })));
      } else if (section === 'nativeIntegration') {
        Object.entries(conversation.sidebarIntegrationOrder ?? {})
          .sort(([, a], [, b]) => a - b)
          .forEach(([id]) =>
            push(
              cardKey({
                cardType: 'native',
                cardId: `partner-integration-${id}`,
              }),
            ),
          );
      } else if (section === 'customIntegration') {
        Object.entries(conversation.sidebarCustomIntegrationOrder ?? {})
          .sort(([, a], [, b]) => a - b)
          .forEach(([id]) => push(cardKey({ cardType: 'custom', cardId: id })));
      }
    },
  );

  const overrides: Record<string, boolean> = {};
  const staticIds = new Set<string>([
    ...defaultCustomerInfoOrder,
    ...defaultKnowledgeOrder,
  ]);

  (conversation.sidebarHiddenItems ?? []).forEach((item) => {
    if (staticIds.has(item)) {
      overrides[cardKey({ cardType: 'static', cardId: item })] = false;
    } else if (item.startsWith('partner-integration-')) {
      overrides[cardKey({ cardType: 'native', cardId: item })] = false;
    } else if (item === 'customerInfo') {
      defaultCustomerInfoOrder.forEach((id) => {
        overrides[cardKey({ cardType: 'static', cardId: id })] = false;
      });
    } else if (item === 'knowledge') {
      defaultKnowledgeOrder.forEach((id) => {
        overrides[cardKey({ cardType: 'static', cardId: id })] = false;
      });
    } else if (item === 'nativeIntegration') {
      Object.keys(conversation.sidebarIntegrationOrder ?? {}).forEach((id) => {
        overrides[
          cardKey({ cardType: 'native', cardId: `partner-integration-${id}` })
        ] = false;
      });
    } else if (item === 'customIntegration') {
      Object.keys(conversation.sidebarCustomIntegrationOrder ?? {}).forEach(
        (id) => {
          overrides[cardKey({ cardType: 'custom', cardId: id })] = false;
        },
      );
    } else {
      overrides[cardKey({ cardType: 'custom', cardId: item })] = false;
    }
  });

  return { order, overrides };
};

/**
 * Migrates conversation sidebar settings from localStorage to user-storage.
 * Covers: `conversationCards`, `isCustomCardMenuOpen`, `elevioCardOpen`, and
 * `knowledgeCardOpen`. Runs once on mount. Skips if user-storage already has
 * sidebar data.
 *
 * TODO: Remove this migration after rollout is complete.
 */
export const useConversationSidebarSettingsMigration = (): void => {
  const storage = useServerStorage('user');
  const { sidebarCardsAdminManagement } = useFeatureFlag([
    'sidebar-cards-admin-management',
  ]);

  // Flat model migration (section → flat). Runs once, after the user storage
  // read completes and only if the flat order has not been written yet. It uses
  // an effect rather than useMount because useMount fires before the async read
  // resolves, leaving storage.data empty, so the migration would silently no-op
  // and never retry.
  const hasMigratedFlatModel = useRef(false);
  useEffect(() => {
    if (
      hasMigratedFlatModel.current ||
      !sidebarCardsAdminManagement ||
      !storage.isSuccess
    ) {
      return;
    }

    const conversation = storage.data.conversation;
    if (!conversation || conversation.sidebarCardOrder !== undefined) {
      return;
    }

    const flat = buildFlatFromLegacy(conversation);
    if (
      !flat ||
      (flat.order.length === 0 && !Object.keys(flat.overrides).length)
    ) {
      return;
    }

    hasMigratedFlatModel.current = true;
    storage.set({
      conversation: (draft) => {
        draft.sidebarCardOrder = flat.order;
        if (Object.keys(flat.overrides).length > 0) {
          draft.sidebarVisibilityOverrides = flat.overrides;
        }
      },
    });
  }, [sidebarCardsAdminManagement, storage]);

  useMount(() => {
    if (
      storage.data.conversation?.sidebarCardOpenState !== undefined ||
      storage.data.conversation?.isSidebarOpen !== undefined
    ) {
      return;
    }

    const localSettings = getLocalSettings();
    if (!localSettings) return;

    const hasSettingsToMigrate =
      !!localSettings.conversationCards ||
      localSettings.isCustomCardMenuOpen != null ||
      localSettings.elevioCardOpen != null ||
      localSettings.knowledgeCardOpen != null;

    if (!hasSettingsToMigrate) return;

    storage.set({
      conversation: (draft) => {
        if (localSettings.conversationCards) {
          draft.sidebarCardOpenState = localSettings.conversationCards;
        }
        if (localSettings.elevioCardOpen != null) {
          draft.sidebarCardOpenState = {
            ...draft.sidebarCardOpenState,
            elevio: localSettings.elevioCardOpen,
          };
        }
        if (localSettings.knowledgeCardOpen != null) {
          draft.sidebarCardOpenState = {
            ...draft.sidebarCardOpenState,
            knowledge: localSettings.knowledgeCardOpen,
          };
        }
        if (localSettings.isCustomCardMenuOpen != null) {
          draft.isSidebarOpen = localSettings.isCustomCardMenuOpen;
        }
      },
    });
  });
};
